import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { ComboboxInputField, DateInputField, FormattedNumberInputField } from "@/components/shared/form";
import { CircleLoader } from "@/components/shared/loaders";
import { useEffect } from "react";
import useAddVacationDaysMutationHook from "@/hooks/mutations/useAddVacationDayMutation";
import { provincesSimplified } from "@/constants/provinces";

const createVacationDaysSchema = (t: TFunction) => z.object({
  province: z.string({ message: t("schemas:form.required") }),
  activeFrom: z.date({
    error: t("schemas:form.required"),
  }),
  days: z.number({
    message: t("schemas:form.required"),
  }).min(0, t("schemas:form.atleast0")),
  lengthOfEmployment: z.number({
    message: t("schemas:form.required"),
  }).min(0, t("schemas:form.atleast0"))
});

interface AddVacationDaysDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

/**
 * AddVacationDaysDialog component for adding a new vacation days
 * @param props Component properties
 * @returns JSX element
 */
function AddVacationDaysDialog({ open, onOpenChange }: AddVacationDaysDialogProps) {
  const { t } = useTranslation(["modals", "common"]);

  const { addVacationDayMutation, loading: createVacationDaysLoading } = useAddVacationDaysMutationHook();

  const vacationDaysSchema = createVacationDaysSchema(t);
  const form = useForm<z.infer<typeof vacationDaysSchema>>({
    resolver: zodResolver(vacationDaysSchema),
    defaultValues: {
      province: undefined,
      activeFrom: undefined,
      days: undefined,
      lengthOfEmployment: undefined,
    },
    mode: "onChange",
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      form.reset({
        province: undefined,
        activeFrom: undefined,
        days: undefined,
        lengthOfEmployment: undefined,
      });
    }
  }, [open, form]);

  const onSubmit = (data: z.infer<typeof vacationDaysSchema>) => {
    const processedData = {
      ...data,
      province: data.province,
      days: data.days,
      lengthOfEmployment: data.lengthOfEmployment,
    };

    addVacationDayMutation({
      variables: { input: processedData },
    }).then(() => {
      onOpenChange(false);
    });
  };

  const onInvalid = (errors: FieldErrors<z.infer<typeof vacationDaysSchema>>) => {
    console.error(errors);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-vacation-day-form">
            <DialogHeader>
              <DialogTitle>
                {t("vacationdays.title")}
              </DialogTitle>
              <DialogDescription>
                {t("vacationdays.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <DateInputField
                  form={form}
                  name="activeFrom"
                  label={t("vacationdays.inputs.activationdate.label")}
                  className="w-full"
                  allowOlderDates={true}
                />
              </div>

              {/* Province */}
              <ComboboxInputField
                form={form}
                name="province"
                label={t("vacationdays.inputs.province.label")}
                options={provincesSimplified.map((province) => ({
                  label: province.name,
                  value: province.code
                }))}
                className="w-full"
              />

              <FormattedNumberInputField
                control={form.control}
                name="days"
                label={t("vacationdays.inputs.days.label")}
                placeholder={t("vacationdays.inputs.days.placeholder")}
                className="w-full"
                style="decimal"
                additionalSuffix={t("vacationdays.inputs.days.suffix")}
                spaceBetweenSuffix={true}
                decimalScale={2}
                isStandalone={true}
              />

              <FormattedNumberInputField
                control={form.control}
                name="lengthOfEmployment"
                label={t("vacationdays.inputs.lengthofemployment.label")}
                placeholder={t("vacationdays.inputs.lengthofemployment.placeholder")}
                className="w-full"
                style="decimal"
                additionalSuffix={t("vacationdays.inputs.lengthofemployment.suffix")}
                spaceBetweenSuffix={true}
                decimalScale={2}
                isStandalone={true}
              />

            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  {t("common:buttons.close")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createVacationDaysLoading}>
                <CircleLoader visible={createVacationDaysLoading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddVacationDaysDialog;
